import { httpMethods } from '@/api';
import { sendApiRequest } from '@/api/sendApiRequest';
import { TContractorsResponse } from '@/contexts/contractorsContext/domain/types';
import {
  TCopyWeeklySchedulePayload,
  TDigitalQueueWeeklyScheduleResponse,
  TDigitalQueueTimeSlots,
  TCreateWeeklyScheduleLayoutsForGatePayload,
  TClearTimeSlotsPayload,
  TGetReservedTimeSlotPayload,
  TGetDigitalQueueSlotFeedsParams,
  TGetBookingsSlotFeedsResponse,
  TSlotFeedsParams,
  TScheduleTemplate,
  TScheduleTemplateUpdatePayload,
  TScheduleTemplateRequestParams,
  TScheduleTemplateLockingPayload,
  TScheduleTemplateReservationPayload,
  TScheduleTemplateReservation,
  TScheduleTemplateTimeSlotsResetResponse,
  TScheduleTemplateReservedTimeSlotPayload,
  TCopyFromScheduleTemplatePayload,
  TSlotsFeedWeeklySchedule,
  TDigitalQueueReservedSlot,
  TCreateShipmentPrebookingPayload,
  TSlotFeedDetails,
  TBookedTimeSlot,
  TDigitalQueue,
  TDigitalQueueActionRequest,
  TDigitalQueueDetails,
  TDigitalQueueGates,
  TDigitalQueueAddGatesPayload,
  TDigitalQueueCreateBookingsRequest,
  TBooking,
  TDigitalQueueReferenceNumbersPayload,
  TDigitalQueueReferenceNumberResponse,
  TDigitalQueueFreightRequestData,
  TDigitalQueueShipmentData,
  TDigitalQueueBookingResponse,
  TAvailableTimeSlotsResponse,
  TDigitalQueueBookingUpdatePayload,
  TDigitalQueueBookingWithShipmentPayload,
  TDigitalQueueBookingWithReferenceNumberPayload,
  TChangeBookingWorkflowStateRequest,
  TBookingWorkflowStateResponse,
  TAvailableBookingWorkflowResponse,
  TBookingWorkflowStatesHistoryRequest,
  TBookingAuditEventResponse,
  TExternalDigitalQueuesResponse,
  TExternalDigitalQueueManifestResponse,
} from '@/contexts/digitalQueuesContext/domain/types';
import {
  TEntityWithFirstSecondLastName,
  TFormSchemaForChangeWorkflowState,
  TResponseWithId,
  TRunWorkflowStateFilterOption,
} from '@/types';
import { TGetGroupsAndPartners } from '@/contexts/executorsLookupContext/domain/types';
import { TFuncApplySetServiceParams } from '@/components/Filter/types';
import { TShipmentBookingPayload } from '@/contexts/orderingContext/views/ShipmentsInfoView/domain/types';
import { ABORT_SIGNAL_NAME } from '@/contexts/digitalQueuesContext/services/constants';

import {
  TCreateLockingArguments,
  TCreateReservationArguments,
  TEditLockingArguments,
  TEditReservationArguments,
} from '../views/DigitalQueueView/components/ManageTimeSlotsDrawer/domain/types';

/** Получить список цифровых очередей */
export const getDigitalQueues = () => sendApiRequest<TDigitalQueue[]>({
  method: httpMethods.GET,
  endpoint: '/:tenant_id/digital_queues',
});

/** Добавить цифровую очередь */
export const addDigitalQueue = (payload: TDigitalQueueActionRequest) => sendApiRequest({
  method: httpMethods.POST,
  endpoint: '/:tenant_id/digital_queues',
  payload,
});

/** Удалить цифровую очередь */
export const deleteDigitalQueue = (digitalQueueId: string) => sendApiRequest({
  method: httpMethods.DELETE,
  endpoint: `/:tenant_id/digital_queues/${digitalQueueId}`,
});

/** Получить детальную информацию о ЦО */
export const getDigitalQueueDetails = (digitalQueueId: string) => sendApiRequest<TDigitalQueueDetails>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/digital_queues/${digitalQueueId}`,
});

/** Обновить детальную информацию о ЦО */
export const updateDigitalQueue = (
  digitalQueueId: string,
  payload: TDigitalQueueActionRequest) => sendApiRequest({
  method: httpMethods.PATCH,
  endpoint: `/:tenant_id/digital_queues/${digitalQueueId}`,
  payload,
});

/** Получить список ворот для ЦО */
export const getDigitalQueueGates = (digitalQueueId: string) => sendApiRequest<TDigitalQueueGates[]>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/gates`,
});

/** Создать ворота для ЦО */
export const addDigitalQueueGates = (digitalQueueId: string, payload: TDigitalQueueAddGatesPayload) => sendApiRequest({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/gates`,
  payload,
});

/** Забронировать тайм-слот в ЦО */
export const bookTimeSlotForDigitalQueue = (
  digitalQueueId: string,
  payload: TDigitalQueueCreateBookingsRequest,
) => sendApiRequest<TBooking[]>({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/bookings`,
  payload,
});

/** Поиск номера транспортировки в ЦО */
export const getDigitalQueueReferenceNumbers = (
  payload: TDigitalQueueReferenceNumbersPayload,
) => sendApiRequest<TDigitalQueueReferenceNumberResponse>({
  method: httpMethods.GET,
  endpoint: '/:tenant_id/autocomplete/digital_queues/freight_requests',
  params: payload,
});

/** Поиск номера транспортировки для самовывозов в ЦО */
export const getDigitalQueueShipmentReferenceNumbers = (
  payload: TDigitalQueueReferenceNumbersPayload,
) => sendApiRequest<TDigitalQueueReferenceNumberResponse>({
  method: httpMethods.GET,
  endpoint: '/:tenant_id/autocomplete/digital_queues/shipments',
  params: payload,
});

/** Получить информацию о точке маршрута для бронирования в ЦО */
export const getTransitPointsData = (
  digitalQueueId: string,
  gateId: string,
  freightRequestIid: number,
) => sendApiRequest<TDigitalQueueFreightRequestData>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/gates/${gateId}/freight_requests/${freightRequestIid}`,
});

/** Получить информацию о самовывозе для бронирования в ЦО */
export const getShipmentData = (
  digitalQueueId: string,
  gateId: string,
  shipmentIid: number,
) => sendApiRequest<TDigitalQueueShipmentData>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/gates/${gateId}/shipments/${shipmentIid}`,
});

/** Получить детальную информацию о бронировании */
export const getBooking = (digitalQueueId: string, bookingId: string) => sendApiRequest<TDigitalQueueBookingResponse>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/bookings/${bookingId}`,
});

// TODO: удалить вместе с FF prebookingReservedSlots
export const annulBooking = (digitalQueueId: string, bookingId: string) => sendApiRequest({
  method: httpMethods.POST,
  endpoint: `:tenant_id/digital_queues/${digitalQueueId}/bookings/${bookingId}/annulation`,
  requestOptions: {
    showError: true,
    serverErrorFirst: true,
  },
});

/** Получить доступные тайм-слоты для ворот */
export const getAvailableTimeSlots = (
  digitalQueueId: string,
  gateId: string,
  exactDate: string,
) => sendApiRequest<TAvailableTimeSlotsResponse>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/gates/${gateId}/available_time_slots`,
  params: { exactDate },
});

/** Обновить тайм-слот для бронирования */
export const updateBookingTimeSlot = ({
  digitalQueueId,
  bookingId,
  payload,
}: {
  digitalQueueId: string,
  bookingId: string,
  payload: TBookedTimeSlot,
}) => sendApiRequest<TDigitalQueueBookingResponse>({
  method: httpMethods.PATCH,
  endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/bookings/${bookingId}/time_slot`,
  payload,
});

/** Обновить информацию по бронированию */
export const updateBooking = (
  digitalQueueId: string,
  bookingId: string,
  payload: TDigitalQueueBookingUpdatePayload,
) => sendApiRequest<TDigitalQueueBookingResponse>({
  method: httpMethods.PUT,
  endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/bookings/${bookingId}`,
  payload,
});

/** Обновить комментарий в бронировании */
export const updateBookingComment = (
  digitalQueueId: string,
  bookingId: string,
  payload: Pick<TDigitalQueueBookingUpdatePayload, 'comment'>,
) => sendApiRequest<Pick<TDigitalQueueBookingResponse, 'comment'>>({
  method: httpMethods.PUT,
  endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/bookings/${bookingId}/comment`,
  payload,
});

/** Редактирование бронирования с транспортным заказом */
export const reassignBooking = ({
  digitalQueueId,
  bookingId,
  transitPointId,
  runUid,
  executionRequestUid,
  comment,
}: TDigitalQueueBookingWithReferenceNumberPayload
& { digitalQueueId: string, bookingId: string },
) => sendApiRequest<TResponseWithId>({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/bookings/${bookingId}/reassign`,
  payload: {
    transitPointId,
    runUid,
    executionRequestUid,
    comment,
  },
});

/** Редактирование бронирования под самовывоз */
export const reassignBookingForShipment = ({
  digitalQueueId,
  bookingId,
  shipmentUid,
}: TDigitalQueueBookingWithShipmentPayload) => sendApiRequest<TResponseWithId>({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/bookings/${bookingId}/reassign`,
  payload: { shipmentUid },
});

/** Заполнение ресурсов самовывоза в ЦО */
// TODO: удалить вместе с FF prebookingReservedSlots
export const fillShipmentBookingResources = (
  digitalQueueId: string,
  shipmentId: string,
  payload: TShipmentBookingPayload,
) => sendApiRequest<TResponseWithId>({
  method: httpMethods.PATCH,
  endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/bookings/shipment_bookings/${shipmentId}`,
  payload,
});

/** Редактирование уже заполненных ресурсов самовывоза в ЦО */
export const editShipmentBookingResources = (
  bookingId: string,
  shipmentId: string,
  payload: TShipmentBookingPayload,
) => sendApiRequest<TResponseWithId>({
  method: httpMethods.PATCH,
  endpoint: `/:tenant_id/shipments/${shipmentId}/bookings/${bookingId}/driver`,
  payload,
});

/** Бронирование таймслота под рейс */
export const bookTimeSlotForRunBooking = (
  digitalQueueId: string,
  payload: TBookedTimeSlot,
) => sendApiRequest<TResponseWithId>({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/bookings/run_booking`,
  payload,
});

/** Перебронирование таймслота под рейс */
export const rebookTimeSlotForRunBooking = (
  digitalQueueId: string,
  payload: TBookedTimeSlot,
) => sendApiRequest({
  method: httpMethods.PUT,
  endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/bookings/run_booking`,
  payload,
});

/** Перебронирование таймслота под самовывоз */
export const rebookTimeSlotForBookingShipment = (
  shipmentId: string,
  bookingId: string,
  payload: TBookedTimeSlot,
) => sendApiRequest({
  method: httpMethods.PUT,
  endpoint: `/:tenant_id/shipments/${shipmentId}/bookings/${bookingId}/time_slot`,
  payload,
});

// TODO: удалить вместе с FF prebookingReservedSlots
export const reserveTimeSlotForExecutionRequest = (
  digitalQueueId: string,
  payload: TBookedTimeSlot,
) => sendApiRequest({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/bookings/execution_request_reservation`,
  payload,
});

// TODO: удалить вместе с FF prebookingReservedSlots
export const reserveTimeSlotForShipment = (
  digitalQueueId: string,
  payload: TBookedTimeSlot,
) => sendApiRequest<TResponseWithId>({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/bookings/shipment_bookings`,
  payload,
});

/** Перебронирование резервирования с транспортным заказом */
export const rebookTimeSlotReservationWithExecutionRequest = (
  digitalQueueId: string,
  payload: TBookedTimeSlot,
) => sendApiRequest<TResponseWithId>({
  method: httpMethods.PATCH,
  endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/bookings/execution_request_reservation`,
  payload,
});

/** Обновление воркфлоу стейта у бронирования */
export function updateBookingWorkflowState(
  digitalQueueId: string,
  bookingId: string,
  payload: TChangeBookingWorkflowStateRequest) {
  return sendApiRequest<TBookingWorkflowStateResponse>({
    method: httpMethods.PUT,
    endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/bookings/${bookingId}/workflow/state`,
    payload,
  });
}

/** Получить доступный воркфлоу стейта у бронирования */
export function getAvailableBookingWorkflow(
  digitalQueueId: string,
  bookingId: string) {
  return sendApiRequest<TAvailableBookingWorkflowResponse>({
    method: httpMethods.GET,
    endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/bookings/${bookingId}/workflow`,
    signalName: ABORT_SIGNAL_NAME.contractorsContextAvailableBookingWorkflow,
  });
}

/** Получить воркфлоу стейта у бронирования */
export function getBookingWorkflowScreen(
  digitalQueueId: string,
  bookingId: string,
  screen: string) {
  return sendApiRequest<TFormSchemaForChangeWorkflowState>({
    method: httpMethods.GET,
    endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/bookings/${bookingId}/workflow/screens/${screen}`,
  });
}

/** Откатить воркфлоу стейт у бронирования */
export function rollbackBookingWorkflowState(
  digitalQueueId: string,
  bookingId: string) {
  return sendApiRequest<TBookingWorkflowStateResponse>({
    method: httpMethods.DELETE,
    endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/bookings/${bookingId}/workflow/state`,
  });
}

/** Получить историю изменений воркфлоу стейта у бронирования */
export function getBookingWorkflowStatesHistory(
  digitalQueueId: string,
  bookingId: string,
) {
  return sendApiRequest<TBookingWorkflowStatesHistoryRequest>({
    method: httpMethods.GET,
    endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/bookings/${bookingId}/workflow/states`,
  });
}

/** Получить историю изменений бронирования */
export const getBookingAuditLogs = (
  digitalQueueId: string,
  bookingId: string,
) => sendApiRequest<TBookingAuditEventResponse>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/bookings/${bookingId}/audit_events`,
});

/** Получить список внешних ЦО */
export const getExternalDigitalQueues = () => sendApiRequest<TExternalDigitalQueuesResponse>({
  method: httpMethods.GET,
  endpoint: ':tenant_id/external_digital_queues',
});

/** Получить манифесты внешних ЦО */
export const getExternalDigitalQueuesManifests = () => sendApiRequest<TExternalDigitalQueueManifestResponse>({
  method: httpMethods.GET,
  endpoint: ':tenant_id/external_digital_queue_manifest',
});

export const createTimeSlotsList = (
  gateId: string,
  year: string,
  weekNumber: string,
  timeSlots: TDigitalQueueTimeSlots,
) => sendApiRequest<{ weeklyScheduleLayoutId: string }>({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedules`,
  payload: {
    year,
    weekNumber,
    timeslots: timeSlots,
  },
});

export const updateTimeSlotsList = (
  gateId: string,
  year: string,
  weekNumber: string,
  timeSlots: TDigitalQueueTimeSlots,
) => sendApiRequest({
  method: httpMethods.PATCH,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedules/${year}/${weekNumber}`,
  payload: { timeslots: timeSlots },
});

export const getDigitalQueueWeeklySchedule = (
  gateId: string,
  year: string,
  weekNumber: string,
) => sendApiRequest<TDigitalQueueWeeklyScheduleResponse>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedules/${year}/${weekNumber}`,
});

export const copyDigitalQueueWeeklySchedule = ({
  gateId,
  year,
  weekNumber,
  ...payload
}: TCopyWeeklySchedulePayload) => sendApiRequest<TResponseWithId>({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedules/${year}/${weekNumber}/copy`,
  payload,
});

/** Очистить таймслоты расписания */
export const clearDigitalQueueTimeSlots = ({
  gateId,
  ...payload
}: TClearTimeSlotsPayload) => sendApiRequest({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/gates/${gateId}/timeslots_lists/cleanings`,
  payload,
});

export const createWeeklyScheduleLayoutsForGate = (
  gateId: string,
  payload: TCreateWeeklyScheduleLayoutsForGatePayload,
) => sendApiRequest({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedule_layouts`,
  payload,
});

export const updateWeeklyScheduleLayoutsForGate = (
  gateId: string,
  payload: TCreateWeeklyScheduleLayoutsForGatePayload,
  weeklyScheduleLayoutId: string,
) => sendApiRequest({
  method: httpMethods.PATCH,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedule_layouts/${weeklyScheduleLayoutId}`,
  payload,
});

export const deleteWeeklyScheduleLayoutsForGate = (
  gateId: string,
  payload: TCreateWeeklyScheduleLayoutsForGatePayload,
  weeklyScheduleLayoutId: string,
) => sendApiRequest({
  method: httpMethods.DELETE,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedule_layouts/${weeklyScheduleLayoutId}`,
  payload,
});

export const createLocking = ({
  gateId,
  year,
  weekNumber,
  payload,
}: TCreateLockingArguments) => sendApiRequest({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedules/${year}/${weekNumber}/lockings`,
  payload,
});

export const createReservations = ({
  gateId,
  year,
  weekNumber,
  payload,
}: TCreateReservationArguments) => sendApiRequest({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedules/${year}/${weekNumber}/reservations`,
  payload,
});

export const editLocking = ({
  gateId,
  year,
  weekNumber,
  id,
  payload,
}: TEditLockingArguments) => sendApiRequest({
  method: httpMethods.PATCH,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedules/${year}/${weekNumber}/lockings/${id}`,
  payload,
});

export const editReservation = ({
  gateId,
  year,
  weekNumber,
  id,
  payload,
}: TEditReservationArguments) => sendApiRequest({
  method: httpMethods.PATCH,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedules/${year}/${weekNumber}/reservations/${id}`,
  payload,
});

/** Отмена резервирования слота */
export const annulWeeklySchedulesReservation = (
  gateId: string,
  weeklySchedulesReservedSlotId: string,
) => sendApiRequest({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedules_reservations/${weeklySchedulesReservedSlotId}/annulations`,
});

/** Отмена блокировки слота */
export const annulWeeklySchedulesLock = (
  gateId: string,
  weeklySchedulesReservedSlotId: string,
) => sendApiRequest({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedules_lockings/${weeklySchedulesReservedSlotId}/annulations`,
});

/** Удаление резервирования слота из шаблона */
export const deleteScheduleTemplateReservation = ({
  id,
  gateId,
  weeklyScheduleTemplateId,
}: TScheduleTemplateReservedTimeSlotPayload) => sendApiRequest({
  method: httpMethods.DELETE,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedule_templates/${weeklyScheduleTemplateId}/reservations/${id}`,
});

/** Удаление блокировки слота из шаблона */
export const deleteScheduleTemplateLock = ({
  id,
  gateId,
  weeklyScheduleTemplateId,
}: TScheduleTemplateReservedTimeSlotPayload) => sendApiRequest({
  method: httpMethods.DELETE,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedule_templates/${weeklyScheduleTemplateId}/lockings/${id}`,
});

/** Получение резервирования */
export const getReservedSlot = ({
  gateId,
  year,
  weekNumber,
  id,
}: TGetReservedTimeSlotPayload) => sendApiRequest<TDigitalQueueReservedSlot>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedules/${year}/${weekNumber}/reservations/${id}`,
});

/** Получение блокировки */
export const getLockingSlot = ({
  gateId,
  year,
  weekNumber,
  id,
}: TGetReservedTimeSlotPayload) => sendApiRequest<TDigitalQueueReservedSlot>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedules/${year}/${weekNumber}/lockings/${id}`,
});

/** Получение списка перевозчиков */
export function getPotentialParticipants({
  query,
  groupIds = [],
  orderIids = [],
  onlyFromGroups,
  withoutGroups,
}: TGetGroupsAndPartners) {
  return sendApiRequest({
    method: httpMethods.GET,
    endpoint: '/:tenant_id/autocomplete/digital_queues/potential_participants',
    params: {
      query,
      groupIds,
      orderIids,
      onlyFromGroups,
      withoutGroups,
    },
  });
}

/** Получение блокировки из шаблона */
export const getScheduleTemplateLockingSlot = ({
  gateId,
  weeklyScheduleTemplateId,
  id,
}: TScheduleTemplateReservedTimeSlotPayload) => sendApiRequest<TScheduleTemplateReservation>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedule_templates/${weeklyScheduleTemplateId}/lockings/${id}`,
});

/** Получение резервирования из шаблона */
export const getScheduleTemplateReservedSlot = ({
  gateId,
  weeklyScheduleTemplateId,
  id,
}: TScheduleTemplateReservedTimeSlotPayload) => sendApiRequest<TScheduleTemplateReservation>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedule_templates/${weeklyScheduleTemplateId}/reservations/${id}`,
});

/* Получения списка всех статусов бронирований */
export const getBookingWorkflowStates = (digitalQueueId: string) => sendApiRequest<TRunWorkflowStateFilterOption[]>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/workflow_states`,
});

/** Получение списка контрагентов */
export const getContractors = (
  query: string,
  category?: string,
  addressId: string | null = null,
) => sendApiRequest<TContractorsResponse>({
  method: httpMethods.GET,
  endpoint: '/:tenant_id/autocomplete/digital_queues/contractors',
  params: {
    query,
    addressId,
    category,
  },
});

/** Получение списка операторов ЦО */
export const getDigitalQueuesEmployees = () => sendApiRequest<TEntityWithFirstSecondLastName[]>({
  method: httpMethods.GET,
  endpoint: '/:tenant_id/autocomplete/digital_queues/employees',
});

/** Получение слотов с бронированиями и резервами */
export const getDigitalQueueSlotFeeds = ({
  digitalQueueId,
  pageItems,
  currentPage,
  sort,
  sortBy,
  filterId,
}: TGetDigitalQueueSlotFeedsParams) => sendApiRequest<TGetBookingsSlotFeedsResponse>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/slot_feeds`,
  params: {
    items: pageItems,
    page: currentPage,
    sort,
    sortBy,
    filterId,
  },
});

/** Получение конкретного слота с бронированиями и резервами */
export const getDigitalQueueSlotFeedById = (
  digitalQueueId: string,
  slotFeedId: string,
) => sendApiRequest<TSlotFeedDetails>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/slot_feeds/${slotFeedId}`,
});

export const getSlotFeedsWeeklySchedule = ({
  digitalQueueId,
  year,
  weekNumber,
  filterId,
}: TGetDigitalQueueSlotFeedsParams & {
  year: string,
  weekNumber: string,
}) => sendApiRequest<TSlotsFeedWeeklySchedule[]>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/weekly_schedules/${year}/${weekNumber}`,
  params: { filterId },
});

export const applyFiltersForBookingsTable = (
  digitalQueueId: string,
  payload: TFuncApplySetServiceParams,
) => sendApiRequest<{ id: string }>({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/slot_feeds/filters`,
  payload,
});

export const createSlotFeedsExportToExcel = ({
  digitalQueueId,
  sort,
  sortBy,
  filterId,
}: TSlotFeedsParams) => sendApiRequest<{ id: string }>({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/digital_queues/${digitalQueueId}/slot_feeds/exports`,
  payload: {
    sort,
    sortBy,
    filterId,
  },
});

/** Получить список шаблонов расписания */
export const getWeeklyScheduleTemplates = (
  { gateId }: { gateId: string },
) => sendApiRequest<TScheduleTemplate[]>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedule_templates`,
});

/** Создать шаблон расписания */
export const createWeeklyScheduleTemplate = (gateId: string, name: string) => sendApiRequest({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedule_templates`,
  payload: { name },
});

/** Обновить шаблон расписания */
export const updateWeeklyScheduleTemplate = ({
  gateId,
  weeklyScheduleTemplateId,
  payload,
}: {
  gateId: string,
  weeklyScheduleTemplateId: string,
  payload: TScheduleTemplateUpdatePayload,
},
) => sendApiRequest({
  method: httpMethods.PATCH,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedule_templates/${weeklyScheduleTemplateId}`,
  payload,
});

/** Удалить шаблон расписания */
export const deleteWeeklyScheduleTemplate = ({
  gateId,
  weeklyScheduleTemplateId,
}: TScheduleTemplateRequestParams) => sendApiRequest({
  method: httpMethods.DELETE,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedule_templates/${weeklyScheduleTemplateId}`,
});

/** Получить информацию по шаблону расписания */
export const getWeeklyScheduleTemplateInfo = ({
  gateId,
  weeklyScheduleTemplateId,
}: TScheduleTemplateRequestParams,
) => sendApiRequest<TScheduleTemplate>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedule_templates/${weeklyScheduleTemplateId}`,
});

/* Создать блокировку в шаблоне */
export const createWeeklyScheduleTemplateLocking = ({
  gateId,
  weeklyScheduleTemplateId,
  payload,
}: {
  gateId: string,
  weeklyScheduleTemplateId: string,
  payload: TScheduleTemplateLockingPayload,
}) => sendApiRequest<TScheduleTemplateReservation[]>({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedule_templates/${weeklyScheduleTemplateId}/lockings`,
  payload,
});

/* Создать резервирование в шаблоне */
export const createWeeklyScheduleTemplateReservation = ({
  gateId,
  weeklyScheduleTemplateId,
  payload,
}: {
  gateId: string,
  weeklyScheduleTemplateId: string,
  payload: TScheduleTemplateReservationPayload,
}) => sendApiRequest<TScheduleTemplateReservation[]>({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedule_templates/${weeklyScheduleTemplateId}/reservations`,
  payload,
});

/* Отредактировать блокировку в шаблоне */
export const updateWeeklyScheduleTemplateLocking = ({
  id,
  gateId,
  weeklyScheduleTemplateId,
  payload,
}: {
  id: string,
  gateId: string,
  weeklyScheduleTemplateId: string,
  payload: TScheduleTemplateLockingPayload,
}) => sendApiRequest<TScheduleTemplateReservation>({
  method: httpMethods.PATCH,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedule_templates/${weeklyScheduleTemplateId}/lockings/${id}`,
  payload,
});

/* Отредактировать резервирование в шаблоне */
export const updateWeeklyScheduleTemplateReservation = ({
  id,
  gateId,
  weeklyScheduleTemplateId,
  payload,
}: {
  id: string,
  gateId: string,
  weeklyScheduleTemplateId: string,
  payload: TScheduleTemplateReservationPayload,
}) => sendApiRequest<TScheduleTemplateReservation>({
  method: httpMethods.PATCH,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedule_templates/${weeklyScheduleTemplateId}/reservations/${id}`,
  payload,
});

/** Очистить таймслоты шаблонов расписания */
export const clearScheduleTemplateTimeSlots = ({
  gateId,
  weeklyScheduleTemplateId,
}: {
  gateId: string,
  weeklyScheduleTemplateId: string,
}) => sendApiRequest<TScheduleTemplateTimeSlotsResetResponse>({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedule_templates/${weeklyScheduleTemplateId}/reset`,
});

/** Копировать расписание из шаблона */
export const copyWeeklyScheduleFromScheduleTemplate = ({
  gateId,
  weeklyScheduleTemplateId,
  payload,
}: {
  gateId: string,
  weeklyScheduleTemplateId: string,
  payload: TCopyFromScheduleTemplatePayload,
}) => sendApiRequest({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/gates/${gateId}/weekly_schedule_templates/${weeklyScheduleTemplateId}/apply`,
  payload,
});

/** Создать предварительное бронирование под самовывоз */
export const createShipmentPrebooking = (
  params: {
    gateId: string,
    year: string,
    weekNumber: string,
  },
  payload: TCreateShipmentPrebookingPayload,
) => sendApiRequest<TResponseWithId>({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/gates/${params.gateId}/weekly_schedules/${params.year}/${params.weekNumber}/shipment_prebookings`,
  payload,
});

/** Аннулировать таймслот */
export const annulSlotFeed = (digitalQueueId: string, slotFeedId: string) => sendApiRequest({
  method: httpMethods.POST,
  endpoint: `:tenant_id/digital_queues/${digitalQueueId}/slot_feeds/${slotFeedId}/annulations`,
});

/** Завершить регистрацию предварительного бронирования */
export const completePrebookingRegistration = (
  gateId: string,
  weeklySchedulesReservedSlotId: string,
  payload: TCreateShipmentPrebookingPayload,
) => sendApiRequest<TResponseWithId>({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/gates/${gateId}/shipment_prebookings/${weeklySchedulesReservedSlotId}/registration`,
  payload,
});

/** Создать предварительное бронирование с транспортным заказом */
export const createTransitPointPrebooking = (
  params: {
    gateId: string,
    year: string,
    weekNumber: string,
  },
  payload: TBookedTimeSlot,
) => sendApiRequest<TResponseWithId>({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/gates/${params.gateId}/weekly_schedules/${params.year}/${params.weekNumber}/transit_point_prebookings`,
  payload,
});
